import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {
    Plus, UserCog, Edit2, Trash2, Send, CheckCircle2, Clock, AlertTriangle, GamepadIcon, Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';

import {supabase} from '@/services/supabase';
import {Button, Table, Badge, FilterBar} from '@/components/ui';
import {CreateHostModal} from '@/components/modals/CreateHostModal';
import {formatDate, isExpired} from '@/utils/formatters';
import {hostAccessBadge} from '@/utils/hostAccess';
import {buildHostFilters} from '@/config/listFilters';
import {useGameHosts, useAccounts, useOrganizations, type GameHostWithAccess} from '@/hooks/api';
import {
    useCreateGameHostWithInvitation,
    useUpdateGameHost,
    useSoftDeleteGameHost,
} from '@/hooks/useHostInvitations';
import {useAccountType} from '@/context/AccountTypeContext';
import {useAuth} from '@/context/AuthContext';
import {requireHostPasswordReset} from '@/utils/adminPasswordReset';
import type {
    GameHost, GameHostFilters, CreateGameHostData, UpdateGameHostData, HostAvailableGame,
} from '@/types';

interface HostListProps {
    /** When set, the list is scoped to one account (Account Detail) — the
     *  account/organization filters and the account column are hidden. */
    fixedAccountId?: string;
    /** Show the Total / Registered / Pending stat cards (global admin page). */
    showStats?: boolean;
}

/**
 * The single game-host list. Backed by the game_hosts_with_access view via
 * useGameHosts, it serves both the global admin page and the account-scoped
 * Account Detail view. Filters come from the shared buildHostFilters config;
 * row actions are the union of both prior components (manage games, invite/
 * resend, require password reset, edit, delete).
 */
export const HostList: React.FC<HostListProps> = ({fixedAccountId, showStats = false}) => {
    const navigate = useNavigate();
    const {labels, accountType} = useAccountType();
    const {user} = useAuth();
    const queryClient = useQueryClient();

    const scoped = Boolean(fixedAccountId);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingGameHost, setEditingGameHost] = useState<GameHost | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState<GameHostFilters>({});
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const createGameHostMutation = useCreateGameHostWithInvitation();
    const updateGameHostMutation = useUpdateGameHost();
    const deleteGameHostMutation = useSoftDeleteGameHost();

    // Reference data for the filter dropdowns + the create modal (global view).
    const {data: accountsResp} = useAccounts(1, 1000, {account_type: accountType});
    const {data: organizationsResp} = useOrganizations(1, 1000, {account_type: accountType});
    const accountsList = accountsResp?.data || [];
    const organizationOptions = (organizationsResp?.data || []).map(o => ({value: o.id, label: o.name}));
    const accountOptions = accountsList.map(a => ({value: a.id, label: a.name}));

    const {data: hostsResp, isLoading} = useGameHosts(1, 1000, {
        ...filters,
        account_type: scoped ? undefined : accountType,
        account_id: fixedAccountId ?? filters.account_id,
        search: searchTerm || undefined,
    }, sortBy, sortOrder);
    const gameHosts = hostsResp?.data || [];

    const totalCount = hostsResp?.total || 0;
    const registeredCount = gameHosts.filter(h => h.invitation_status === 'accepted').length;
    const pendingCount = gameHosts.filter(h => h.invitation_status !== 'accepted').length;

    const handleSort = (column: string, order: 'asc' | 'desc') => {
        setSortBy(column);
        setSortOrder(order);
    };
    const handleOpenModal = (host: GameHost | null = null) => {
        setEditingGameHost(host);
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingGameHost(null);
    };
    const handleDelete = (host: GameHost) => {
        if (window.confirm(`Are you sure you want to delete ${host.first_name} ${host.last_name}?`)) {
            deleteGameHostMutation.mutate(host.id);
        }
    };
    const handleSendInvitation = async (host: GameHost) => {
        try {
            const {data: availableGames, error} = await supabase
                .rpc('get_host_available_games', {p_host_email: host.email});
            if (error) console.warn('Failed to fetch existing game access:', error);
            const gameAccess: string[] = (availableGames as HostAvailableGame[] | null)?.map(g => g.game_name) || [];
            createGameHostMutation.mutate({
                email: host.email,
                first_name: host.first_name,
                last_name: host.last_name,
                phone: host.phone,
                account_id: host.account_id,
                account_type: host.account_type,
                game_access: gameAccess,
                send_invitation: true,
            });
        } catch (err) {
            console.error('Error in handleSendInvitation:', err);
            toast.error('Failed to prepare invitation');
        }
    };
    const handleRequirePasswordReset = (host: GameHost) => {
        if (host.invitation_status !== 'accepted') {
            toast.error('Host must accept invitation before requiring password reset');
            return;
        }
        if (window.confirm(`Require ${host.first_name} ${host.last_name} to reset their password on next login?`)) {
            requireHostPasswordReset(host.email, user?.id)
                .then(() => {
                    toast.success(`Password reset required for ${host.email}`);
                    queryClient.invalidateQueries({queryKey: ['game-hosts']});
                })
                .catch((error) => toast.error(`Failed to require password reset: ${error.message}`));
        }
    };

    // Close modal on successful create/update.
    useEffect(() => {
        if (createGameHostMutation.isSuccess || updateGameHostMutation.isSuccess) handleCloseModal();
    }, [createGameHostMutation.isSuccess, updateGameHostMutation.isSuccess]);

    const invitationBadge = (record: GameHostWithAccess) => {
        if (record.invitation_status === 'accepted') {
            return (
                <div className="flex items-center space-x-1 text-green-600">
                    <CheckCircle2 className="h-4 w-4"/>
                    <span className="text-sm">Registered</span>
                </div>
            );
        }
        if (record.invitation_expires_at && isExpired(record.invitation_expires_at)) {
            return (
                <div className="flex items-center space-x-1 text-red-600">
                    <AlertTriangle className="h-4 w-4"/>
                    <span className="text-sm">Expired</span>
                </div>
            );
        }
        if (record.invitation_sent_at) {
            return (
                <div className="flex items-center space-x-1 text-blue-600">
                    <Clock className="h-4 w-4"/>
                    <span className="text-sm">Sent {formatDate(record.invitation_sent_at) || 'recently'}</span>
                </div>
            );
        }
        return (
            <div className="flex items-center space-x-1 text-yellow-600">
                <Clock className="h-4 w-4"/>
                <span className="text-sm">Not Sent</span>
            </div>
        );
    };

    const columns: Array<{
        key: string;
        title: string;
        sortable?: boolean;
        render?: (value: any, record: GameHostWithAccess) => React.ReactNode;
    }> = [
        {
            key: 'first_name',
            title: labels.hostSingular,
            sortable: true,
            render: (_, record) => (
                <div className="flex items-start space-x-3">
                    <UserCog className="h-5 w-5 text-purple-500 mt-0.5"/>
                    <div>
                        <button
                            onClick={() => navigate(`/host-permissions/${record.id}`)}
                            className="font-medium text-gray-900 hover:text-blue-600 transition-colors text-left"
                        >
                            {record.first_name} {record.last_name}
                        </button>
                        <p className="text-sm text-gray-500">{record.email}</p>
                        {record.phone && <p className="text-sm text-gray-400">{record.phone}</p>}
                    </div>
                </div>
            ),
        },
        ...(scoped ? [] : [{
            key: 'account_name',
            title: labels.accountSingular,
            sortable: true,
            render: (_: any, record: GameHostWithAccess) => (
                <div>
                    <p className="font-medium text-gray-900">{record.account_name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                        {record.organization_name || 'N/A'}, {record.organization_state || 'N/A'}
                    </p>
                </div>
            ),
        }]),
        {
            key: 'invitation_status',
            title: 'Registration',
            sortable: true,
            render: (_, record) => invitationBadge(record),
        },
        {
            key: 'access_status',
            title: 'Access',
            sortable: true,
            render: (_, record) => {
                const badge = hostAccessBadge(record, labels);
                return <Badge variant={badge.variant}>{badge.label}</Badge>;
            },
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => {
                const host = record as unknown as GameHost;
                return (
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate(`/host-permissions/${record.id}`)}
                            className="p-1 text-gray-600 hover:text-green-600 transition-colors"
                            title="Manage Games"
                        >
                            <GamepadIcon className="h-4 w-4"/>
                        </button>
                        {record.invitation_status === 'accepted' && (
                            <button
                                onClick={() => handleRequirePasswordReset(host)}
                                className="p-1 text-gray-600 hover:text-orange-600 transition-colors"
                                title="Require Password Reset"
                            >
                                <Shield className="h-4 w-4"/>
                            </button>
                        )}
                        {record.invitation_status !== 'accepted' && (
                            <button
                                onClick={() => handleSendInvitation(host)}
                                className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                                title="Send/Resend Invitation"
                                disabled={createGameHostMutation.isPending}
                            >
                                <Send className="h-4 w-4"/>
                            </button>
                        )}
                        <button
                            onClick={() => handleOpenModal(host)}
                            className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                            title="Edit"
                        >
                            <Edit2 className="h-4 w-4"/>
                        </button>
                        <button
                            onClick={() => handleDelete(host)}
                            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                            title="Delete"
                        >
                            <Trash2 className="h-4 w-4"/>
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            {showStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total {labels.hostPlural}</p>
                                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                            </div>
                            <UserCog className="h-8 w-8 text-purple-500"/>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Registered</p>
                                <p className="text-2xl font-bold text-green-600">{registeredCount}</p>
                            </div>
                            <CheckCircle2 className="h-8 w-8 text-green-500"/>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Pending</p>
                                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
                            </div>
                            <Clock className="h-8 w-8 text-yellow-500"/>
                        </div>
                    </div>
                </div>
            )}

            <FilterBar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={`Search ${labels.hostPlural.toLowerCase()}...`}
                filters={buildHostFilters({
                    labels,
                    filters,
                    onChange: (patch) => setFilters(prev => ({...prev, ...patch})),
                    organizationOptions,
                    accountOptions,
                    includeReferenceFilters: !scoped,
                })}
            />

            <div className="flex justify-end">
                <Button onClick={() => handleOpenModal()} className="flex items-center space-x-2">
                    <Plus className="h-4 w-4"/>
                    <span>Add {labels.hostSingular}</span>
                </Button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {gameHosts.length === 0 && !isLoading ? (
                    <div className="text-center py-12">
                        <UserCog className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                        <p className="text-gray-500">No {labels.hostPlural.toLowerCase()} found</p>
                    </div>
                ) : (
                    <Table
                        data={gameHosts}
                        columns={columns}
                        loading={isLoading}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                    />
                )}
            </div>

            <CreateHostModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={(data) => {
                    if (editingGameHost) {
                        updateGameHostMutation.mutate({id: editingGameHost.id, data: data as UpdateGameHostData});
                    } else {
                        createGameHostMutation.mutate(data as CreateGameHostData);
                    }
                }}
                editingHost={editingGameHost}
                fixedAccountId={fixedAccountId}
                accounts={accountsList}
                isLoading={createGameHostMutation.isPending || updateGameHostMutation.isPending}
                hostSingular={labels.hostSingular}
            />
        </div>
    );
};
